import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Crepe, CrepeFeature } from "@milkdown/crepe";
import { remarkStringifyOptionsCtx } from "@milkdown/kit/core";
import { Milkdown, MilkdownProvider, useEditor } from "@milkdown/react";
import { useQuery, useAction } from "convex/react";
import { useAuthToken } from "@convex-dev/auth/react";
import { Eye, FileText, Loader2 } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useProjectUIStore } from "@/features/project/stores/project-ui-store";
import { FileTree } from "@/features/project/components/file-tree";
import type { FileNode } from "@/features/project/types";
import {
  resolveEditorContent,
  shouldAdoptServerContent,
  shouldClearLocalDraftOnServerAdvance,
} from "@/features/project/lib/editor-session-guards";
import { api } from "@/lib/convex-api";

/* Crepe features: code (CodeMirror), list, link, block, table, cursor. BlockEdit is disabled on touch (no mouse) where it doesn't work. */
const CREPE_FEATURES_BASE: Partial<Record<CrepeFeature, boolean>> = {
  [CrepeFeature.CodeMirror]: true,
  [CrepeFeature.ListItem]: true,
  [CrepeFeature.LinkTooltip]: true,
  [CrepeFeature.BlockEdit]: true,
  [CrepeFeature.Table]: true,
  [CrepeFeature.Cursor]: true,
  [CrepeFeature.Placeholder]: true,
};

const SAVE_DEBOUNCE_MS = 800;
type PendingSave = { path: string; content: string; revision: number };

interface EditorAreaProps {
  readOnly?: boolean;
  fileTree?: FileNode[];
}

export const EditorArea = ({ readOnly = false, fileTree }: EditorAreaProps) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const selectedFilePath = useProjectUIStore((s) => s.selectedFilePath);
  const projectId = useProjectUIStore((s) => s.projectId);
  const draftsInvalidatedAt = useProjectUIStore((s) => s.draftsInvalidatedAt);
  const draftClearRequest = useProjectUIStore((s) => s.draftClearRequest);

  const isTouchDevice = useMediaQuery("(pointer: coarse)");
  const token = useAuthToken();

  // Load file content from Convex
  const fileData = useQuery(
    api.projectFiles.read,
    projectId && selectedFilePath
      ? { projectId: projectId as any, path: selectedFilePath }
      : "skip",
  );
  const tombstoneData = useQuery(
    api.projectFiles.readTombstone,
    projectId && selectedFilePath
      ? { projectId: projectId as any, path: selectedFilePath }
      : "skip",
  );

  const writeFile = useAction(api.projectFiles.write);

  const [initialContentForFile, setInitialContentForFile] = useState("");
  const draftByPathRef = useRef<Record<string, string>>({});
  const seededContentByPathRef = useRef<Record<string, string>>({});
  const lastSeenServerContentByPathRef = useRef<Record<string, string>>({});
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRevisionRef = useRef(0);
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const previousSelectedFilePathRef = useRef<string | null>(null);
  const previousDraftsInvalidatedAtRef = useRef(draftsInvalidatedAt);
  const previousDraftClearRequestedAtRef = useRef(0);
  const latestProjectIdRef = useRef(projectId);
  const latestTokenRef = useRef(token);
  const writeFileRef = useRef(writeFile);
  const [editorResetVersion, setEditorResetVersion] = useState(0);

  useEffect(() => {
    if (!selectedFilePath) return;
  }, [selectedFilePath, fileData?.content, tombstoneData?.deletedContent]);

  useEffect(() => {
    if (!draftClearRequest || !selectedFilePath) return;
    if (
      draftClearRequest.requestedAt <= previousDraftClearRequestedAtRef.current
    )
      return;
    previousDraftClearRequestedAtRef.current = draftClearRequest.requestedAt;
    if (draftClearRequest.path !== selectedFilePath) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingSaveRef.current = null;
    saveRevisionRef.current += 1;
    delete draftByPathRef.current[selectedFilePath];
  }, [draftClearRequest, selectedFilePath]);

  useEffect(() => {
    latestProjectIdRef.current = projectId;
  }, [projectId]);

  useEffect(() => {
    latestTokenRef.current = token;
  }, [token]);

  useEffect(() => {
    writeFileRef.current = writeFile;
  }, [writeFile]);

  const persistDraft = useCallback((path: string, content: string) => {
    const currentProjectId = latestProjectIdRef.current;
    if (!currentProjectId) return;

    // Persist editor content to Convex for the given path.
    void writeFileRef
      .current({
        projectId: currentProjectId as any,
        token: latestTokenRef.current ?? "",
        path,
        content,
      })
      .then((result) => {
        if (result.status.flags.includes("INTEGRATION_BLOCKED")) {
          console.warn("Write blocked by integration state.");
        }
      })
      .catch((err) => console.error("Failed to save file:", err));
  }, []);

  const flushPendingSave = useCallback(() => {
    const pending = pendingSaveRef.current;
    if (!pending) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    pendingSaveRef.current = null;
    saveRevisionRef.current += 1;
    persistDraft(pending.path, pending.content);
  }, [persistDraft]);

  // Set initial content when file changes or drafts are invalidated (e.g. after Discard All)
  useEffect(() => {
    const didPathChange =
      previousSelectedFilePathRef.current !== selectedFilePath;
    const didDraftsInvalidate =
      draftsInvalidatedAt > previousDraftsInvalidatedAtRef.current;

    if (didPathChange) {
      flushPendingSave();
    }
    previousSelectedFilePathRef.current = selectedFilePath;
    previousDraftsInvalidatedAtRef.current = draftsInvalidatedAt;

    if (!selectedFilePath) return;
    if (didDraftsInvalidate) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      pendingSaveRef.current = null;
      saveRevisionRef.current += 1;
      draftByPathRef.current = {};
      setEditorResetVersion((prev) => prev + 1);
    }

    // If the server advances and our local draft is just an echo of the last
    // server version, drop it so the editor can fast-forward (e.g. agent edit).
    const previousServerContent =
      lastSeenServerContentByPathRef.current[selectedFilePath];
    const nextServerContent =
      tombstoneData?.deletedContent ?? fileData?.content;
    if (
      shouldClearLocalDraftOnServerAdvance({
        draftContent: draftByPathRef.current[selectedFilePath],
        previousServerContent,
        nextServerContent,
      })
    ) {
      delete draftByPathRef.current[selectedFilePath];
      setEditorResetVersion((prev) => prev + 1);
    }
    if (nextServerContent !== undefined) {
      lastSeenServerContentByPathRef.current[selectedFilePath] =
        nextServerContent;
    }

    const hasLocalDraft = Object.prototype.hasOwnProperty.call(
      draftByPathRef.current,
      selectedFilePath,
    );
    const shouldAdopt = shouldAdoptServerContent({
      didPathChange,
      draftsInvalidated: didDraftsInvalidate,
      hasLocalDraft,
    });
    if (!shouldAdopt) return;

    const initial = resolveEditorContent({
      draftContent: draftByPathRef.current[selectedFilePath],
      fileContent: fileData?.content,
      tombstoneContent: tombstoneData?.deletedContent,
    });
    seededContentByPathRef.current[selectedFilePath] = initial;
    setInitialContentForFile((prev) => (prev === initial ? prev : initial));
  }, [
    selectedFilePath,
    fileData?.content,
    tombstoneData?.deletedContent,
    draftsInvalidatedAt,
    draftClearRequest?.requestedAt,
    flushPendingSave,
  ]);

  // Flush pending save on unmount so navigation away doesn't drop edits.
  useEffect(
    () => () => {
      flushPendingSave();
    },
    [flushPendingSave],
  );

  const handleContentChange = useCallback(
    (value: string) => {
      if (!selectedFilePath || !projectId) return;

      const seededValue = seededContentByPathRef.current[selectedFilePath];
      if (seededValue !== undefined && value === seededValue) {
        return;
      }
      draftByPathRef.current[selectedFilePath] = value;

      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      const rev = ++saveRevisionRef.current;
      pendingSaveRef.current = {
        path: selectedFilePath,
        content: value,
        revision: rev,
      };
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        if (rev !== saveRevisionRef.current) return;
        const pending = pendingSaveRef.current;
        if (!pending || pending.revision !== rev) return;

        pendingSaveRef.current = null;
        // Debounced save — persist to Convex
        persistDraft(pending.path, pending.content);
      }, SAVE_DEBOUNCE_MS);
    },
    [selectedFilePath, projectId, persistDraft],
  );

  if (!selectedFilePath) {
    return <EmptyState fileTree={fileTree} />;
  }

  const isDeleted = Boolean(!fileData && tombstoneData);

  // Show loading while file metadata/content is being fetched
  if (fileData === undefined && tombstoneData === undefined && projectId) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isDeleted) {
    const deletedContent = tombstoneData?.deletedContent ?? "";
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 flex justify-center overflow-auto min-h-0">
          <MilkdownProvider>
            <MilkdownCrepeEditor
              key={`deleted-${selectedFilePath}`}
              initialContent={deletedContent}
              disableBlockEdit={false}
              allowAutoFocus={isDesktop}
              readOnly
            />
          </MilkdownProvider>
        </div>
      </div>
    );
  }

  // Viewers see read-only content (no editing or saving)
  if (readOnly) {
    const content = fileData?.content ?? "";
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 flex justify-center overflow-auto min-h-0">
          <MilkdownProvider>
            <MilkdownCrepeEditor
              key={`readonly-${selectedFilePath}`}
              initialContent={content}
              disableBlockEdit={false}
              allowAutoFocus={isDesktop}
              readOnly
            />
          </MilkdownProvider>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Editor body — Milkdown Crepe with its built-in toolbar */}
      <div className="flex-1 flex flex-col items-center  overflow-auto min-h-0">
        <MilkdownProvider>
          <MilkdownCrepeEditor
            key={`${selectedFilePath}-${editorResetVersion}`}
            initialContent={initialContentForFile}
            onContentChange={handleContentChange}
            disableBlockEdit={isTouchDevice}
            allowAutoFocus={isDesktop}
          />
        </MilkdownProvider>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Milkdown Crepe editor                                              */
/* ------------------------------------------------------------------ */

interface MilkdownCrepeEditorProps {
  initialContent: string;
  onContentChange?: (value: string) => void;
  disableBlockEdit: boolean;
  allowAutoFocus: boolean;
  readOnly?: boolean;
}

const MilkdownCrepeEditor = ({
  initialContent,
  onContentChange,
  disableBlockEdit,
  allowAutoFocus,
  readOnly = false,
}: MilkdownCrepeEditorProps) => {
  const features = useMemo(
    () => ({
      ...CREPE_FEATURES_BASE,
      [CrepeFeature.BlockEdit]: !disableBlockEdit,
    }),
    [disableBlockEdit],
  );

  useEditor(
    (root) => {
      const crepe = new Crepe({
        root,
        defaultValue: initialContent,
        features,
        featureConfigs: {
          [CrepeFeature.Placeholder]: {
            text: "Start typing something...",
          },
        },
      });
      if (readOnly) {
        crepe.setReadonly(true);
      }
      crepe.editor.config((ctx) => {
        ctx.update(remarkStringifyOptionsCtx, (prev) => ({
          ...prev,
          bullet: "-" as const,
          bulletOther: "*" as const,
          rule: "-" as const,
          ruleRepetition: 3,
          ruleSpaces: false,
        }));
      });

      if (!readOnly && onContentChange) {
        crepe.on((api) => {
          api.markdownUpdated((_ctx, markdown) => {
            onContentChange(markdown);
          });
        });
      }

      return crepe;
    },
    [initialContent, features, disableBlockEdit, readOnly, onContentChange],
  );

  useEffect(() => {
    if (allowAutoFocus) return;
    // Prevent mobile soft keyboard from opening immediately on document load.
    const blurIfEditorFocused = () => {
      const active = document.activeElement as HTMLElement | null;
      if (active?.closest(".milkdown")) {
        active.blur();
      }
    };
    const raf = window.requestAnimationFrame(blurIfEditorFocused);
    const timeout = window.setTimeout(blurIfEditorFocused, 50);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
    };
  }, [allowAutoFocus, initialContent]);

  return <Milkdown />;
};

/* ------------------------------------------------------------------ */
/*  Read-only banner for viewers                                        */
/* ------------------------------------------------------------------ */

export const ReadOnlyBanner = () => (
  <div className="flex shrink-0 items-center gap-2 border-b bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
    <Eye className="size-3.5 shrink-0" />
    <span>You have view-only access. Editing is disabled.</span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Empty state                                                         */
/* ------------------------------------------------------------------ */

const EmptyState = ({ fileTree }: { fileTree?: FileNode[] }) =>
  fileTree ? (
    <div className="flex h-full flex-col">
      <p className="px-3 py-3 text-sm font-medium text-muted-foreground">
        Choose a file to start editing
      </p>
      <div className="flex-1 min-h-0">
        <FileTree nodes={fileTree} />
      </div>
    </div>
  ) : (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
      <FileText className="size-10 opacity-30" />
      <p className="text-sm font-medium">No file selected</p>
      <p className="text-xs opacity-60">
        Choose a file from the sidebar to start editing
      </p>
    </div>
  );
